import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getDirectSalesPoiPoints } from 'store/dealManagement/selectors/directSales';
import { Asset } from 'components/common/types/Asset.types';
import { DirectSalesAsset } from 'components/common/types/DirectSalesCampaign.types';
import { PlannerPoi } from 'components/common/types/Planner.types';
import { coordinateToPosition, POICluster } from '../POIClusters.types';
import { usePOIWorker } from './usePOIWorker';
import { UsePOIResult } from './usePOI.types';

export interface UseDirectSalesPOIOptions {
  skipClustering?: boolean;
}

export const useDirectSalesPOI = (
  assets: DirectSalesAsset[],
  { skipClustering }: UseDirectSalesPOIOptions = { skipClustering: false },
): UsePOIResult => {
  const poiPoints = useSelector(getDirectSalesPoiPoints);
  const [isGeneratingPOIClusters, setIsGeneratingPOIClusters] = useState(false);
  const [poiClusters, setPoiClusters] = useState<POICluster[]>([]);

  const { generateDirectSalesClusters, isReady: isWorkerReady } = usePOIWorker();

  const generateClustersForDirectSales = useCallback(async () => {
    if (!poiPoints || poiPoints.length === 0 || !isWorkerReady) {
      setPoiClusters([]);
      setIsGeneratingPOIClusters(false);
      return;
    }

    setIsGeneratingPOIClusters(true);

    try {
      const clusters = await generateDirectSalesClusters(poiPoints);
      setPoiClusters(clusters);
    } catch (error) {
      setPoiClusters([]);
    } finally {
      setIsGeneratingPOIClusters(false);
    }
  }, [poiPoints, generateDirectSalesClusters, isWorkerReady]);

  useEffect(() => {
    if (skipClustering) return;

    generateClustersForDirectSales();
  }, [skipClustering, generateClustersForDirectSales]);

  const poiData: PlannerPoi[] = useMemo(() => {
    if (skipClustering) return poiPoints || [];
    if (isGeneratingPOIClusters || !isWorkerReady) return [];
    if (!poiClusters || poiClusters.length === 0) return poiPoints || [];

    return poiClusters.map((cluster, index) => ({
      id: `cluster-${index}`,
      name: `${cluster.name} (${cluster.pois.length})`,
      geometry: { coordinates: coordinateToPosition(cluster.centerCoordinates) },
      properties: {
        ...cluster.pois[0]?.properties,
        frameId: `${cluster.name}-frame-${index}`,
        visibility: cluster.visibility,
        cluster: true,
        point_count: cluster.pois.length,
        categoryName: cluster.name,
      },
    }));
  }, [poiClusters, poiPoints, isGeneratingPOIClusters, isWorkerReady, skipClustering]);

  return useMemo<UsePOIResult>(
    () => ({
      isGeneratingPOIClusters,
      filteredAssets: assets as Asset[],
      poiData,
      onClusterAssetsUpdate: undefined,
      onMapDataUpdate: undefined,
    }),
    [isGeneratingPOIClusters, assets, poiData],
  );
};
